import {ApolloQueryResult} from "@apollo/client";
import BackendError from "../../BackendError";
import {IvrsQuery, useIvrsQuery} from "@/queries.generated";
import ErrorHelper from "../../../utils/ErrorHelper";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import {UpdateIvrMutationVariables} from "../queries.generated";
import {FormattedPageTitle} from "../../pages";
import NumberDisplay from "../../Number";
import {
    ErrorWithContext,
    FormActionButtons,
    FormattedFormRow,
    FormContainer,
    PendingWrapper,
    TextField,
} from "@dvrt/graphql-widgets";
import {useFormGuard} from "../../../utils/formGuard";
import {DirectionType, Media, NonPaymentBlockingType, UpdateIvrAnnouncement} from "@/api";
import {EnumField, NumberField} from "@dvrt/graphql-widgets";
import OutgoingCallBarringWidget from "../../../outgoing-call-barring/OutgoingCallBarringWidget";
import {DeepPartial, MAX_I32, saveAudio} from "@/tools";
import Loading from "@/components/breadcrumbs/Loading";
import IvrSettingsForm from "./Form";
import {Dispatch, useState} from "react";
import {DomainOutgoingCallBarrings, useDomainNameQuery, useDomainOutgoingCallBarringsQuery} from "@/hooks";
import {IconButton, styled, useTheme} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useDispatch} from "react-redux";
import DomainMediaSelector from "@/components/DomainMediaSelector";
import {defineMessage} from "react-intl";
import {DIRECTION_TYPE_MESSAGES, NON_PAYMENT_BLOCKING_TYPE_MESSAGES} from "@/utils/translations";

type Ivr = IvrsQuery["ivrs"]["items"][number];

type Props = {
    id: number;
    domainId: number;
};

type ContentProps = {
    refetch: () => Promise<ApolloQueryResult<IvrsQuery>>;
    ivr: Ivr;
    domainName: string;
    domainId: number;
    domainOutgoingCallBarrings: DomainOutgoingCallBarrings;
};

function createInitialData(ivr: IvrsQuery["ivrs"]["items"][number]): UpdateIvrMutationVariables {
    if (!ivr) {
        return {
            id: 0,
            name: "",
            outgoingCallBarringAdmin: [],
            outgoingCallBarringOperator: [],
            recordingType: DirectionType.None,
            callLimit: 0,
            nonPaymentBlockingType: NonPaymentBlockingType.NotBlocked,
            privateCallerAnnouncement: {
                unset: false,
            },
        };
    }

    return {
        id: ivr.id,
        name: ivr.name,
        outgoingCallBarringAdmin: ivr.outgoingCallBarringAdmin,
        outgoingCallBarringOperator: ivr.outgoingCallBarringOperator,
        recordingType: ivr.recordingType,
        callLimit: ivr.callLimit,
        nonPaymentBlockingType: ivr.number.nonPaymentBlockingType,
        privateCallerAnnouncement: {
            id: undefined,
            file: undefined,
            description: ivr.privateCallerAnnouncement?.description,
            unset: false,
        },
    };
}

const Content = ({ivr, refetch, domainName, domainId, domainOutgoingCallBarrings}: ContentProps) => {
    const [initialData, setInitialData] = useState(createInitialData(ivr));
    const [pending, setPending] = useState(false);
    const auth = useAuth();
    const canEditOperator = auth.hasPermission("edit_superuser_ivr", ContextFilters.domain(domainName));
    const dispatch = useDispatch();

    const form = new IvrSettingsForm(initialData);
    const errorHelper = new ErrorHelper(form.errors);
    useFormGuard(form.hasChanged);

    async function submit() {
        setPending(true);

        if (!(await form.submit())) {
            setPending(false);
            return;
        }

        const result = await refetch();
        const newInitialData = createInitialData(result.data.ivrs.items[0]);
        form.newInitialData(newInitialData);
        setPending(false);
        setInitialData(newInitialData);
    }

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={defineMessage({id: "ivr.settings", defaultMessage: "Settings for auto attendant {number}"})}
                values={{
                    number: ivr ? <NumberDisplay e164={ivr.number.e164} shortNumber={ivr.number.shortNumber} /> : "",
                }}
            />
            <FormContainer style={{maxWidth: 500}}>
                <FormattedFormRow label={defineMessage({id: "name", defaultMessage: "Name"})}>
                    <TextField
                        id="name"
                        value={form.values.name}
                        disabled={pending}
                        onChange={(value) => {
                            form.updateValues({name: value});
                        }}
                        errors={errorHelper.fieldErrors("name")}
                    />
                </FormattedFormRow>

                <FormattedFormRow
                    label={defineMessage({id: "DirectionType.label", defaultMessage: "Recording direction"})}
                >
                    <EnumField
                        disabled={!canEditOperator || pending}
                        value={form.values.recordingType}
                        enum_={{DirectionType}}
                        onChange={(val) =>
                            form.updateValues({
                                recordingType: val as DirectionType,
                            })
                        }
                        translations={DIRECTION_TYPE_MESSAGES}
                    />
                </FormattedFormRow>

                <FormattedFormRow
                    label={defineMessage({
                        id: "ivr.private_caller_announcement",
                        defaultMessage: "Reject private caller with",
                    })}
                >
                    <AnnouncementField
                        field={form.values.privateCallerAnnouncement}
                        initialField={ivr?.privateCallerAnnouncement}
                        errors={errorHelper.fieldErrors("privateCallerAnnouncement")}
                        onChange={(field) => form.updateValues({privateCallerAnnouncement: field})}
                        accessToken={auth.accessToken}
                        dispatch={dispatch}
                        domainId={domainId}
                    />
                </FormattedFormRow>

                <FormattedFormRow
                    label={defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"})}
                >
                    <OutgoingCallBarringWidget
                        masterBarrings={domainOutgoingCallBarrings}
                        operatorBarrings={form.values.outgoingCallBarringOperator}
                        adminBarrings={form.values.outgoingCallBarringAdmin}
                        updateOperatorBarrings={(barrings) =>
                            form.updateValues({outgoingCallBarringOperator: barrings})
                        }
                        updateAdminBarrings={(barrings) => form.updateValues({outgoingCallBarringAdmin: barrings})}
                        disabledAdmin={pending}
                        disabledOperator={!canEditOperator || pending}
                    />
                </FormattedFormRow>

                <FormattedFormRow label={defineMessage({id: "callLimit", defaultMessage: "Parallel call limit"})}>
                    <NumberField
                        disabled={!canEditOperator || pending}
                        margin="dense"
                        fullWidth
                        max={MAX_I32}
                        min={0}
                        value={form.values.callLimit}
                        update={(newValue) => form.updateValues({callLimit: newValue})}
                        errors={errorHelper.fieldErrors("callLimit")}
                    />
                </FormattedFormRow>

                <FormattedFormRow
                    label={defineMessage({id: "NonPaymentBlockingType.label", defaultMessage: "Non-payment blocking"})}
                >
                    <EnumField
                        disabled={!canEditOperator || pending}
                        value={form.values.nonPaymentBlockingType}
                        enum_={{NonPaymentBlockingType}}
                        onChange={(val) =>
                            form.updateValues({
                                nonPaymentBlockingType: val as NonPaymentBlockingType,
                            })
                        }
                        translations={NON_PAYMENT_BLOCKING_TYPE_MESSAGES}
                    />
                </FormattedFormRow>

                <FormattedFormRow>
                    <div style={{marginTop: "1em"}}>
                        <FormActionButtons
                            mainAction={submit}
                            secondaryAction={() => form.reset()}
                            enabled={form.hasChanged && !pending}
                            pending={pending}
                        />
                    </div>
                </FormattedFormRow>
            </FormContainer>
        </div>
    );
};

const AnnouncementField = ({
    field,
    initialField,
    errors,
    onChange,
    accessToken,
    dispatch,
    domainId,
}: {
    field: UpdateIvrAnnouncement;
    initialField: Omit<Media, "mimeType" | "domainId">;
    errors: ErrorWithContext[];
    onChange: (field: DeepPartial<UpdateIvrAnnouncement>) => void;
    accessToken: string;
    dispatch: Dispatch<any>;
    domainId: number;
}) => {
    const theme = useTheme();
    const [downloading, setDownloading] = useState(false);

    const showDownload = !field?.file && Boolean(initialField?.downloadToken) && !field?.unset;
    const showDelete = (field?.file || initialField?.downloadToken) && !field?.unset;
    const mediaSelectorValue = field?.unset ? undefined : (field?.file ?? field?.id ?? initialField?.id);

    const handleDownload = async () => {
        const downloadToken = initialField?.downloadToken;
        if (downloading || !downloadToken) return;
        setDownloading(true);

        const filename = initialField?.description ?? "private_caller_announcement";
        await saveAudio(downloadToken, filename, accessToken, dispatch);

        setDownloading(false);
    };

    const handleDelete = () => {
        onChange({
            file: undefined,
            description: undefined,
            // if there is no current announcement, then there is no need to delete it
            // also in that case it won't make the form resetable
            unset: initialField?.downloadToken ? true : false,
        });
    };

    const handleOnFileSelect = (file: File) => {
        if (!file) return;
        const description = file.name.split(".").slice(0, -1).join(".");

        onChange({
            file,
            description,
            unset: false,
        });
    };

    return (
        <AnnouncementRow>
            <DomainMediaSelector
                domainId={domainId}
                value={mediaSelectorValue}
                errors={errors}
                onChange={(v) => {
                    if (v instanceof File) {
                        handleOnFileSelect(v);
                    } else {
                        onChange({
                            file: undefined,
                            id: v.id,
                            description: v.description,
                            unset: false,
                        });
                    }
                }}
            />
            {showDownload && (
                <PendingWrapper pending={downloading}>
                    <IconButton onClick={handleDownload}>
                        <FontAwesomeIcon icon="download" color={theme.palette.primary.main} />
                    </IconButton>
                </PendingWrapper>
            )}
            {showDelete && (
                <IconButton onClick={handleDelete}>
                    <FontAwesomeIcon icon="times" color={theme.palette.error.main} />
                </IconButton>
            )}
        </AnnouncementRow>
    );
};

const AnnouncementRow = styled("div")({
    display: "flex",
    alignItems: "start",
    columnGap: "10px",
});

const IvrSuperuserSettings = ({id, domainId}: Props) => {
    const {data, loading, error, refetch} = useIvrsQuery({variables: {id}});
    const {domainName, loading: domainNameLoading, error: domainNameError} = useDomainNameQuery(domainId);
    const {domainOutgoingCallBarrings, loading: domainOutgoingCallBarringsLoading} =
        useDomainOutgoingCallBarringsQuery(domainId);

    const ivr = data?.ivrs.items[0];

    if (error || domainNameError) return <BackendError error={error} />;

    if (loading || domainNameLoading || domainOutgoingCallBarringsLoading) return <Loading />;

    return (
        <Content
            ivr={ivr}
            refetch={refetch}
            domainName={domainName}
            domainId={domainId}
            domainOutgoingCallBarrings={domainOutgoingCallBarrings}
        />
    );
};

export default IvrSuperuserSettings;
